import {
	Avatar,
	Box,
	Card,
	CardActionArea,
	Chip,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Roll } from "../../models/Roll";
import { RollPassStatus } from "../RollPassStatus";
import { rollPositionCodes } from "../RollPositionSelector";
import { rollStatusCodes } from "../RollStatusSelector";

type Props = {
	roll: Roll;
	selected?: boolean;
	onClick?: () => void;
	standId?: number;
	sectionId?: number;
};

export const RollListItem = ({
	roll,
	selected = false,
	onClick,
	standId,
	sectionId,
}: Props) => {
	const status = rollStatusCodes.find((r) => r.Code === roll.RollStatusCode);
	const isInRollset = roll.RollsetId != null;
	const isInCarrier = ["A", "S", "C", "I"].includes(roll.RollStatusCode);
	const positionName = rollPositionCodes.find(
		(p) => p.Code === roll.RollPosition,
	)?.Name;

	const compatiblePasses = sectionId
		? roll.Passes?.filter((pass) =>
				pass.Sections?.some((section) => section.SectionId === sectionId),
			).map((pass) => pass.PassNumber)
		: [];

	const isSectionCompatible = (() => {
		if (!sectionId) return null;
		if (roll.RollTypeCode === "E") {
			if (roll.Passes.length == 0) return null;
			return compatiblePasses && compatiblePasses.length > 0;
		} else {
			if (!roll.SectionsId) return false;
			const sectionsIds = roll.SectionsId.split(",").map((id) => id.trim());
			return sectionsIds.includes(sectionId.toString());
		}
	})();

	const isStandCompatible = standId != null && roll.StandId === standId;

	const standTooltip = isStandCompatible
		? "Compatible with stand"
		: "Not compatible with stand";

	const sectionTooltip = (() => {
		if (!isSectionCompatible) return "Not compatible with section";
		if (
			roll.RollTypeCode === "E" &&
			compatiblePasses &&
			compatiblePasses.length > 0
		) {
			const passLabel = compatiblePasses.length === 1 ? "pass" : "passes";
			return `Compatible with section on ${passLabel} ${compatiblePasses.join(", ")}`;
		}
		return "Compatible with section";
	})();

	const cardContent = (
		<Stack
			direction="row"
			spacing={2}
			alignItems="center"
			sx={{ px: 2, py: 1.5 }}
		>
			<Avatar
				sx={{
					width: 28,
					height: 28,
					fontSize: "0.75rem",
					fontWeight: 600,
					bgcolor: selected ? "primary.main" : "grey.200",
					color: selected ? "white" : "text.secondary",
				}}
			>
				{roll.RollTypeCode ?? "?"}
			</Avatar>

			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography variant="body1" fontWeight={600} noWrap>
					{roll.PartName}
				</Typography>
				<Stack
					direction="row"
					alignItems="center"
					spacing={0.75}
					sx={{ mt: 0.5 }}
				>
					{status && (
						<Chip
							label={status.Name}
							size="small"
							sx={{
								height: 22,
								fontSize: "0.75rem",
								bgcolor: status.Color,
								color: status.TextColor,
							}}
						/>
					)}
					{positionName && (
						<Chip
							label={positionName}
							size="small"
							sx={{
								height: 22,
								fontSize: "0.75rem",
								backgroundColor: rollPositionCodes.find(
									(p) => p.Code === roll.RollPosition,
								)?.Color,
								color: roll.RollPosition === "T" ? "black" : "white",
							}}
						/>
					)}
					{isInRollset && (
						<Chip
							label={isInCarrier ? "Carrier" : "Rollset"}
							size="small"
							variant="outlined"
							sx={{ height: 22, fontSize: "0.75rem" }}
						/>
					)}
					{(standId != null || sectionId != null) && (
						<Box
							sx={{
								width: "1px",
								height: 16,
								bgcolor: "divider",
								mx: 0.5,
							}}
						/>
					)}
					{standId != null && roll.StandId != null && (
						<Tooltip title={standTooltip}>
							<Chip
								icon={
									isStandCompatible ? (
										<CheckIcon sx={{ fontSize: "0.85rem !important" }} />
									) : (
										<CloseIcon sx={{ fontSize: "0.85rem !important" }} />
									)
								}
								label="Stand"
								size="small"
								color={isStandCompatible ? "success" : "error"}
								variant="filled"
								sx={{ height: 22, fontSize: "0.75rem" }}
							/>
						</Tooltip>
					)}
					{sectionId != null && isSectionCompatible !== null && (
						<Tooltip title={sectionTooltip}>
							<Chip
								icon={
									isSectionCompatible ? (
										<CheckIcon sx={{ fontSize: "0.85rem !important" }} />
									) : (
										<CloseIcon sx={{ fontSize: "0.85rem !important" }} />
									)
								}
								label="Section"
								size="small"
								color={isSectionCompatible ? "info" : "error"}
								variant="filled"
								sx={{ height: 22, fontSize: "0.75rem" }}
							/>
						</Tooltip>
					)}
				</Stack>
			</Box>

			<Stack
				direction="row"
				spacing={3}
				alignItems="center"
				sx={{ flexShrink: 0 }}
			>
				{roll.Diameter && (
					<Typography variant="caption" color="text.secondary">
						<Box component="span">{roll.Diameter.toFixed(3)}</Box>
					</Typography>
				)}
				{roll.Passes && (
					<RollPassStatus
						passesStatus={roll.Passes.map((p) => p.PassStatusCode).join(",")}
						withLabel={false}
					/>
				)}
			</Stack>
		</Stack>
	);

	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: 0,
				borderBottom: "1px solid",
				borderColor: "divider",
				bgcolor: selected ? "action.selected" : "transparent",
				"&:hover": onClick ? { bgcolor: "action.hover" } : {},
			}}
		>
			{onClick ? (
				<CardActionArea onClick={onClick}>{cardContent}</CardActionArea>
			) : (
				cardContent
			)}
		</Card>
	);
};
